import {Badge, Box, ClickAwayListener, Popper} from '@mui/material';
import {GetProgressResponse} from 'domain/usecases/private/profile/get-progress';
import {queryClient} from 'infra/libs/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {FeaturesEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useAccountInformation, useMediaQueries} from 'presentation/pages';
import {formatText, getInitials, getOnlyFirstWord} from 'presentation/utils';
import {getPendingPercentage} from 'presentation/utils/getPendingPercentage';
import React, {Fragment, MouseEvent, useState} from 'react';
import {PROFILE_PROGRESS_THRESHOLDS} from './components/CompleteProfileCard/constants';
import {ProfileMenuContent} from './ProfileMenuContent';
import {ProfileMenuLegacy} from './ProfileMenuLegacy';
import {
	ArrowIcon,
	GradientAvatarWrapper,
	ProfileContainer,
	ProfileIcon,
	StyledBeneficiaryName,
	StyledBox,
	StyledDrawer,
	StyledInitials,
	StyledPopperPaper,
} from './styles';
import {useShowFacialBiometric} from 'presentation/pages/private/hooks/useShowFacialBiometric';

export function ProfileMenu() {
	const [anchorElProfile, setAnchorElProfile] = useState<null | HTMLElement>(null);
	const {beneficiary, accesses, isHolder, odontoOnly, canChangeAddress} = useAccountInformation();
	const {isGndiSaoPaulo} = useHostName();
	const {isMobile} = useMediaQueries();
	const {isEnabled: isMultiThemeFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NDI_MULTI_THEME,
	);

	const {isEnabled: isNewProfileFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_NEW_PROFILE,
	);

	const beneficiaryNameFormatted = getOnlyFirstWord(
		formatText(beneficiary?.nmSocialUsuarioC || beneficiary?.nmUsuarioC),
	);

	const showBankData = accesses.refund && isHolder && !odontoOnly && isGndiSaoPaulo;
	const showFacialBiometric = useShowFacialBiometric();
	const handleOpenProfileMenu = (event: MouseEvent<HTMLElement>) => {
		setAnchorElProfile(event.currentTarget);
	};

	const handleCloseProfileMenu = () => {
		setAnchorElProfile(null);
	};

	const progressData: GetProgressResponse | undefined = queryClient.getQueryData([
		QUERY_KEYS.PROGRESS_PROFILE,
		showBankData,
		canChangeAddress,
		showFacialBiometric,
	]);

	const pendingPercentage = getPendingPercentage({
		totalFields: progressData?.totalFields || 0,
		filledFields: progressData?.filledFields || 0,
	});

	if (isNewProfileFeatureFlagEnabled) {
		return (
			<Fragment>
				<Box display="inline-flex">
					<ProfileContainer
						size="small"
						onClick={anchorElProfile ? handleCloseProfileMenu : handleOpenProfileMenu}
						data-testid="profile-menu-button">
						<GradientAvatarWrapper>
							<Badge
								color="error"
								overlap="circular"
								badgeContent=""
								sx={{
									'& .MuiBadge-badge': {
										minWidth: 14,
										height: 14,
										display:
											pendingPercentage < PROFILE_PROGRESS_THRESHOLDS.MAX
												? 'flex'
												: 'none',
									},
								}}>
								<ProfileIcon alt="Profile Avatar">
									<StyledInitials color="primary">
										{getInitials(
											beneficiary?.nmSocialUsuarioC ||
												beneficiary?.nmUsuarioC,
										)}
									</StyledInitials>
								</ProfileIcon>
							</Badge>
						</GradientAvatarWrapper>
						<StyledBox
							textAlign="start"
							mx={0.5}
							color="white"
							$isGndiSaoPaulo={isGndiSaoPaulo}>
							<StyledBeneficiaryName variant="subtitle2">
								{beneficiaryNameFormatted}
							</StyledBeneficiaryName>
						</StyledBox>
						<ArrowIcon
							name={
								isMultiThemeFeatureFlagEnabled
									? 'chevron-right-outlined'
									: 'arrow-down-filled'
							}
							size={20}
						/>
					</ProfileContainer>
					{anchorElProfile && !isMobile ? (
						<ClickAwayListener onClickAway={handleCloseProfileMenu}>
							<Popper
								open={Boolean(anchorElProfile)}
								anchorEl={anchorElProfile}
								placement="bottom-end"
								sx={{zIndex: 1200}}>
								<StyledPopperPaper elevation={8}>
									<ProfileMenuContent onClose={handleCloseProfileMenu} />
								</StyledPopperPaper>
							</Popper>
						</ClickAwayListener>
					) : null}
				</Box>

				{isMobile ? (
					<StyledDrawer
						anchor="right"
						open={Boolean(anchorElProfile)}
						onClose={handleCloseProfileMenu}>
						<ProfileMenuContent onClose={handleCloseProfileMenu} />
					</StyledDrawer>
				) : null}
			</Fragment>
		);
	}

	return <ProfileMenuLegacy />;
}
